import React, { useState } from 'react';

export default function Contact() {
  return (
    <section className="relative min-h-screen pt-32 pb-24 px-6" style={{ background: '#082318' }}>
      {/* Glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,245,66,0.06) 0%, transparent 65%)' }}
      />

      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-mono uppercase tracking-widest mb-4 font-semibold" style={{ color: '#d4f542' }}>Contact</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-5 leading-tight">
            Let's Build Something<br className="hidden md:block" /> Together
          </h1>
          <p className="text-base max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.52)' }}>
            Whether you need a complete digital overhaul or a smart AI agent, we're ready to help you scale.
          </p>
        </div>

        {/* Main card */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{ background: '#0c2d1e', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="grid gap-0 lg:grid-cols-2">
            {/* Left info panel */}
            <div
              className="p-10 lg:p-14 flex flex-col justify-between"
              style={{ borderRight: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div>
                <h2 className="text-2xl font-bold text-white mb-4">Start a conversation</h2>
                <p className="text-sm leading-relaxed mb-10" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  We typically respond within 24 hours. Tell us about your project and let's make it happen.
                </p>

                <div className="space-y-7">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.38)' }}>Email Us</p>
                    <a href="mailto:hello@synex.agency" className="text-lg font-semibold text-white hover:opacity-70 transition">
                      hello@synex.agency
                    </a>
                  </div>
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.38)' }}>Call Us</p>
                    <a href="tel:+1234567890" className="text-lg font-semibold text-white hover:opacity-70 transition">
                      +1 (234) 567-890
                    </a>
                  </div>
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-3 mt-12">
                {['AI Automation', 'Web Design', 'CRM Setup', '24/7 Support'].map(tag => (
                  <span
                    key={tag}
                    className="px-4 py-1.5 rounded-full text-xs font-medium"
                    style={{ background: 'rgba(212,245,66,0.1)', color: '#d4f542', border: '1px solid rgba(212,245,66,0.2)' }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Right form panel */}
            <div className="p-10 lg:p-14">
              <ContactForm />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [business, setBusiness] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setStatus({ type: 'error', message: 'Please fill in your name, email, and message.' });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, business, message }),
      });

      if (!res.ok) {
        const error = await res.json().catch(() => null);
        throw new Error(error?.error || 'Failed to send message');
      }

      setStatus({ type: 'success', message: 'Thanks! Your message has been received.' });
      setName('');
      setEmail('');
      setBusiness('');
      setMessage('');
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: err.message || 'Could not send your message.' });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: '#082318',
    border: '1px solid rgba(255,255,255,0.12)',
    color: '#ffffff',
    borderRadius: '12px',
    padding: '14px 18px',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    transition: 'border-color 0.2s',
  };

  const labelStyle = { fontSize: '13px', fontWeight: '500', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', display: 'block' };

  return (
    <form className="flex flex-col gap-5" onSubmit={onSubmit}>
      <div>
        <label style={labelStyle}>Full Name</label>
        <input value={name} onChange={e => setName(e.target.value)} type="text" style={inputStyle} placeholder="John Doe" disabled={submitting}
          onFocus={e => e.target.style.borderColor = 'rgba(212,245,66,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
        />
      </div>

      <div>
        <label style={labelStyle}>Email Address</label>
        <input value={email} onChange={e => setEmail(e.target.value)} type="email" style={inputStyle} placeholder="john@company.com" disabled={submitting}
          onFocus={e => e.target.style.borderColor = 'rgba(212,245,66,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
        />
      </div>

      <div>
        <label style={labelStyle}>Business Name <span style={{ color: 'rgba(255,255,255,0.3)' }}>(optional)</span></label>
        <input value={business} onChange={e => setBusiness(e.target.value)} type="text" style={inputStyle} placeholder="Your Company" disabled={submitting}
          onFocus={e => e.target.style.borderColor = 'rgba(212,245,66,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
        />
      </div>

      <div>
        <label style={labelStyle}>Message</label>
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows="5" style={{ ...inputStyle, resize: 'vertical' }} placeholder="Tell us about your project..." disabled={submitting}
          onFocus={e => e.target.style.borderColor = 'rgba(212,245,66,0.5)'}
          onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.12)'}
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full py-4 rounded-xl text-sm font-bold transition duration-200 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
        style={{ background: '#d4f542', color: '#082318' }}
        onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#c8e830'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#d4f542'; }}
      >
        {submitting ? 'Sending…' : 'Send Message'}
      </button>

      {status && (
        <div
          className="rounded-xl px-4 py-3 text-sm font-medium"
          style={status.type === 'success'
            ? { background: 'rgba(212,245,66,0.1)', color: '#d4f542', border: '1px solid rgba(212,245,66,0.2)' }
            : { background: 'rgba(239,68,68,0.1)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.2)' }
          }
        >
          {status.message}
        </div>
      )}
    </form>
  );
}

